import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { postAnswer } from "../../api/answer";
import { getQuizById, getQuizIdsByScenario } from "../../api/quiz";
import handGood from "../../assets/hand-good.png";
import ScenarioBar from "../../components/ScenarioBar";
import { useGameTexts } from "../../hooks/useGameTexts";
import { useTimer } from "../../hooks/useTimer";
import { updateScore, updateTime } from "../../redux/actions";
import { useLang } from "../../translations/i18n";
import { QUIZZES, ROUTES, SCENARIOS, SCORE_TYPES } from "../../utils/constants";
import {
	getScenarioByScreen,
	getTimer,
	updateStats,
} from "../../utils/helpers";
import {
	alertError,
	modalGeneric,
	modalGenericSmall,
	modalQuestionInfo,
} from "../../utils/logger";

const QUESTION_EXPLANATION = `<div class="text-center txt-black">
		<div class="txt-turquoise bold">
			¿Qué recomiendan otras sociedades científicas acerca de la duración
			del tratamiento?
		</div>
		<div class="d-flex justify-content-center mt-4">
			<div class="bg-correct p-2 w-fit-content">
				<img width="25" src=${handGood} class="me-1" />
				<span class="bold">+10</span> puntos
			</div>
		</div>
		<div class="mt-4">
			Vamos a plantearte 2 enunciados que pueden ser Verdaderos o Falsos.
		</div>
		<div class="txt-turquoise bold mt-4">¿Preparado?</div>
	</div>`;

const QUIZ_ID = QUIZZES.SCENARIO7;
const SCENARIO_ID = SCENARIOS.SCENARIO_7;
const NAVIGATE_TO = ROUTES.SCENARIO7_QUESTION2;

const CORRECT_ANSWER_POINTS = 50;

function shuffleArray(arr) {
	const a = [...arr];
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
}

function mapQuizToOptions(quizData) {
	if (!quizData || !Array.isArray(quizData.options) || quizData.options.length < 9) return null;
	const labels = Array.isArray(quizData.filterLabels) && quizData.filterLabels.length >= 3
		? quizData.filterLabels
		: ["Análogos del ácido micofenólico (MPAA)", "Ciclofosfamida", "Inhibidores de la calcineurina (ICN)"];
	return [0, 1, 2].map((i) => ({
		id: i + 1,
		text: labels[i] || "",
		answers: quizData.options.slice(i * 3, i * 3 + 3).map((o) => ({
			id: o.id,
			text: o.text || "",
			selected: false,
			correct: !!o.correct,
		})),
	}));
}

function Scenario7({ intl }) {
	const lang = useLang();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const participant = useSelector((state) => state.group);
	const cachedQuiz = useSelector((state) => state.quizCache?.[SCENARIO_ID]);

	const scenarioNum = getScenarioByScreen(location.pathname);
	const { texts, ready: textsReady } = useGameTexts(participant?.game);
	const instruction = texts["scenario7.instruction"] ?? "";
	const explanationHtml = texts["scenario7.explanation"] ?? "";

	const [options, setOptions] = useState([]);
	const [intentos, setIntentos] = useState(1);
	const [isCompleted, setIsCompleted] = useState(false);
	const [resolvedQuizId, setResolvedQuizId] = useState(null);
	const [time, setTime] = useState(0);

	useEffect(() => {
		const gameId = participant?.game;
		if (!gameId) return;

		if (cachedQuiz?.data) {
			const mapped = mapQuizToOptions(cachedQuiz.data);
			if (mapped) setOptions(shuffleArray(mapped));
			if (cachedQuiz.resolvedQuizId) setResolvedQuizId(cachedQuiz.resolvedQuizId);
			return;
		}

		let cancelled = false;
		const params = { game: gameId };
		getQuizIdsByScenario(SCENARIO_ID, params)
			.then((listRes) => {
				if (cancelled) return;
				const list = listRes?.data && Array.isArray(listRes.data) ? listRes.data : [];
				const id = list[0]?._id || QUIZ_ID;
				setResolvedQuizId(id);
				return getQuizById(id);
			})
			.then((res) => {
				if (cancelled) return;
				if (res?.status === 200 && res?.data) {
					const mapped = mapQuizToOptions(res.data);
					if (mapped) setOptions(shuffleArray(mapped));
				}
			})
			.catch(() => {});
		return () => { cancelled = true; };
	}, [participant?.game, cachedQuiz?.data, location.key]);

	useTimer(
		() => {
			setTime(time + 1);
		},
		isCompleted ? null : 1000
	);

	const SelectOption = ({ id }) => {
		let option = options.find((x) => x.id === id);

		return (
			<div>
				<div
					className="bg-white p-3 bold txt-black txt18 d-flex align-items-center justify-content-center text-center"
					style={{ borderRadius: "4px", height: "10vh" }}
				>
					{option?.text}
				</div>
				{option?.answers?.map((answer) => {
					const selected = answer?.selected;

					function getBackgroundColor() {
						return answer?.selected &&
							answer?.correct &&
							isCompleted
							? "#D6D431"
							: answer?.selected &&
							  !answer?.correct &&
							  isCompleted
							? "#FF5948"
							: answer?.selected
							? "#FF7F6B"
							: "#FFFFFF";
					}

					function handleOptionClick() {
						if (isCompleted) return;

						let updatedOptions = options.map((x) =>
							x.id === id
								? {
										...x,
										answers: x?.answers?.map((a) =>
											a.id === answer?.id
												? {
														...a,
														selected: !a.selected,
												  }
												: { ...a, selected: false }
										),
								  }
								: { ...x, selected: false }
						);

						setOptions(updatedOptions);
					}

					return (
						<div
							className={`d-flex align-items-center justify-content-start txt-black mt-3 p-3 ${
								selected ? "option-selected txt-white bold" : ""
							}`}
							style={{
								height: "fit-content",
								lineHeight: "20px",
								borderRadius: "4px",
								backgroundColor: getBackgroundColor(),
								opacity: selected ? "1" : "0.8",
							}}
							onClick={handleOptionClick}
						>
							<div className="d-flex align-items-start justify-content-start">
								<div className="ms-2">{answer?.text}</div>
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	function checkAnswers() {
		if (!options?.every((x) => x?.answers?.some((y) => y.selected))) {
			alertError({
				error: " ",
				customMessage:
					"Para avanzar, debes seleccionar una opción para cada elemento.",
			});
			return;
		}

		const wrongAnswers = options?.filter((x) =>
			x?.answers?.some((y) => y.selected && y?.selected !== y?.correct)
		);
		const correct = wrongAnswers?.length === 0;

		const explanation = `<div class="text-left txt14 px-5">${explanationHtml}</div>`;

		if (correct) {
			const text = `<div class="txt14 txt-black">
			<div class="text-center bold txt-turquoise txt18">
				¡Correcto!
			</div>
			<div class="d-flex justify-content-center mt-3">
				<div class="bg-correct p-2 w-fit-content">
					<img width="25" src=${handGood} class="me-1" />
					<span class="bold">+${CORRECT_ANSWER_POINTS}</span> puntos
				</div>
			</div>
			${explanation}
		</div>`;

			modalGeneric({ html: text }).then((res) => {
				if (res.isConfirmed) submitAnswer({ correct: correct });
			});
		} else {
			const text = `<div class="text-center">
					<div class="bold txt-turquoise">
						${
							intentos < 3
								? `Hay ${wrongAnswers?.length} fallo(s).`
								: "¡Vaya! Aún hay algún fallo."
						}					
					</div>					
					<div class="txt-black mt-3">${
						intentos < 3 ? "Inténtalo otra vez." : explanation
					}</div>
				</div>`;

			if (intentos < 3) modalGenericSmall({ html: text });
			else
				modalGeneric({ html: text }).then((res) => {
					if (res.isConfirmed) submitAnswer({ correct: correct });
				});
		}
		setIntentos(intentos + 1);
	}

	async function submitAnswer({ correct }) {
		setIsCompleted(true);

		const score = correct && intentos <= 3 ? CORRECT_ANSWER_POINTS : 0;

		let totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));
		postAnswer({
			scenario: SCENARIO_ID,
			quiz: resolvedQuizId ?? QUIZ_ID,
			group: participant?._id,
			score: score,
			time: time,
			correct: correct,
		})
			.then((res) => {
				if (res.status === 201) {
					dispatch(updateScore(score, SCORE_TYPES.SCORE));

					updateStats({
						...participant,
						time: totalTime,
						score: participant?.score + score,
						lastVisited: NAVIGATE_TO,
					});
				}
			})
			.catch((error) => {});
	}

	return (
		<div className="h-100 bg-prueba">
			<div className="container">
				<div className="row h-100">
					<div className="d-flex justify-content-center txtWhite txt20 bold">
						{!textsReady ? "Cargando..." : instruction}
					</div>
					<div className="row d-flex justify-content-center align-items-start mt-4">
						{options.length === 0 ? (
							<div className="txt-white">Cargando opciones...</div>
						) : (
							options.map((option) => (
								<div key={option.id} className="col">
									<SelectOption id={option.id} />
								</div>
							))
						)}
					</div>
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					right: "4vh",
					bottom: "17vh",
					zIndex: 1000,
				}}
			>
				<div className="d-flex justify-content-end">
					<div
						className="btn-main"
						onClick={() => {
							if (isCompleted)
								modalQuestionInfo({
									html: QUESTION_EXPLANATION,
								}).then((res) => {
									if (res.isConfirmed) navigate(NAVIGATE_TO);
								});
							else checkAnswers();
						}}
					>
						<div className="text-btn-main">AVANZAR</div>
					</div>
				</div>
			</div>
			<ScenarioBar activeScenario={scenarioNum}></ScenarioBar>
		</div>
	);
}

export default injectIntl(Scenario7);
